// Kuryo Design Tokens (Faz 3)
// Tek kaynak: createTheme bunlardan beslenir.
// Marka: primary #E02F39 (kırmızı), secondary #1F2937 (slate).

export const palette = {
  primary: {
    50:  '#FCE7E9',
    100: '#F8C8CC',
    200: '#F19BA1',
    300: '#E96A73',
    400: '#E44851',
    500: '#E02F39',  // main
    600: '#C42830',
    700: '#A8232B',
    800: '#7C1A20',
    900: '#4F1115',
    main: '#E02F39',
    light: '#FCE7E9',
    dark: '#A8232B',
    contrastText: '#FFFFFF',
  },
  secondary: {
    50:  '#F3F4F6',
    100: '#E5E7EB',
    200: '#D1D5DB',
    300: '#9CA3AF',
    400: '#6B7280',
    500: '#4B5563',
    600: '#374151',
    700: '#1F2937',  // main
    800: '#111827',
    900: '#0B1220',
    main: '#1F2937',
    light: '#374151',
    dark: '#111827',
    contrastText: '#FFFFFF',
  },
  surface: {
    canvas: '#F7F8FA',
    raised: '#FFFFFF',
    sunken: '#EEF0F4',
    border: '#E5E7EB',
    divider: '#EAECEF',
    overlay: 'rgba(17, 24, 39, 0.45)',
  },
  text: {
    primary:   '#111827',
    secondary: '#4B5563',
    disabled:  '#9CA3AF',
    inverse:   '#FFFFFF',
  },
  status: {
    success: '#16A34A',
    successBg: '#DCFCE7',
    warning: '#D97706',
    warningBg: '#FEF3C7',
    error:   '#DC2626',
    errorBg: '#FEE2E2',
    info:    '#2563EB',
    infoBg:  '#DBEAFE',
  },
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 40,
};

export const radius = {
  sm: 8,
  md: 10,
  lg: 14,
  pill: 999,
};

export const shadow = {
  e0: 'none',
  e1: '0 1px 2px rgba(17,24,39,0.04), 0 1px 3px rgba(17,24,39,0.06)',
  e2: '0 2px 4px rgba(17,24,39,0.06), 0 4px 12px rgba(17,24,39,0.08)',
};

export const typography = {
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif',
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  scale: {
    h1: { fontSize: 32, lineHeight: 1.2,  fontWeight: 700 },
    h2: { fontSize: 26, lineHeight: 1.25, fontWeight: 700 },
    h3: { fontSize: 22, lineHeight: 1.3,  fontWeight: 600 },
    h4: { fontSize: 18, lineHeight: 1.35, fontWeight: 600 },
    h5: { fontSize: 16, lineHeight: 1.4,  fontWeight: 600 },
    h6: { fontSize: 14, lineHeight: 1.4,  fontWeight: 600 },
    body1: { fontSize: 14, lineHeight: 1.5 },
    body2: { fontSize: 13, lineHeight: 1.5 },
    caption:{ fontSize: 12, lineHeight: 1.4 },
    button: { fontSize: 14, fontWeight: 600, textTransform: 'none' },
  },
};

// MUI createTheme'a verilecek tam yapı
export function buildMuiThemeOptions() {
  return {
    palette: {
      primary:   palette.primary,
      secondary: palette.secondary,
      background: {
        default: palette.surface.canvas,
        paper:   palette.surface.raised,
      },
      text: {
        primary:   palette.text.primary,
        secondary: palette.text.secondary,
        disabled:  palette.text.disabled,
      },
      divider: palette.surface.divider,
      error:   { main: palette.status.error },
      warning: { main: palette.status.warning },
      success: { main: palette.status.success },
      info:    { main: palette.status.info },
    },
    shape: { borderRadius: radius.md },
    typography: {
      fontFamily: typography.fontFamily,
      h1: typography.scale.h1,
      h2: typography.scale.h2,
      h3: typography.scale.h3,
      h4: typography.scale.h4,
      h5: typography.scale.h5,
      h6: typography.scale.h6,
      body1: typography.scale.body1,
      body2: typography.scale.body2,
      caption: typography.scale.caption,
      button: typography.scale.button,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: palette.surface.canvas },
          // a11y: brand-tinted keyboard focus ring (skip mouse-only focus)
          '*:focus-visible': {
            outline: `2px solid ${palette.primary.main}`,
            outlineOffset: 2,
            borderRadius: radius.sm,
          },
          'a:focus-visible, button:focus-visible': {
            outline: `2px solid ${palette.primary.main}`,
            outlineOffset: 2,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            paddingInline: 18,
            boxShadow: 'none',
            transition: 'background-color 200ms ease, box-shadow 200ms ease, transform 200ms ease',
            '&:hover': { boxShadow: 'none' },
            '&:active': { transform: 'translateY(1px)' },
            minHeight: 36, // a11y: 24x24 min target, comfortable 36
          },
          containedPrimary: {
            backgroundColor: palette.primary.main,
            '&:hover': { backgroundColor: palette.primary.dark },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'background-color 200ms ease, transform 200ms ease',
            '&:active': { transform: 'scale(0.95)' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.lg,
            boxShadow: shadow.e1,
            border: `1px solid ${palette.surface.border}`,
            transition: 'box-shadow 200ms ease, transform 200ms ease',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: radius.md },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: shadow.e0 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: palette.surface.sunken,
            color: palette.text.primary,
            fontWeight: 600,
            fontSize: 13,
          },
          body: { fontSize: 13 },
        },
      },
    },
  };
}

const tokens = { palette, spacing, radius, shadow, typography, buildMuiThemeOptions };
export default tokens;
